import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { QrCode, History, BarChart2, CheckCircle, XCircle, Loader2, LogOut, Crown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useCurrency } from '../context/CurrencyContext';

function ScanTab({ storeInfo }) {
  const [scannerActive, setScannerActive] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [billTotal, setBillTotal] = useState('');
  const [redeemAmount, setRedeemAmount] = useState('');
  const scannerRef = useRef(null);
  const html5ScannerRef = useRef(null);
  const { symbol, formatMoney } = useCurrency();

  const startScanner = async () => {
    setScannerActive(true);
    setTimeout(async () => {
      try {
        const { Html5QrcodeScanner } = await import('html5-qrcode');
        html5ScannerRef.current = new Html5QrcodeScanner(
          'qr-reader',
          { fps: 10, qrbox: { width: 250, height: 250 } },
          false
        );
        html5ScannerRef.current.render(
          (decodedText) => {
            html5ScannerRef.current?.clear().catch(() => {});
            setScannerActive(false);
            handleVerify(decodedText);
          },
          () => {}
        );
      } catch (err) {
        toast.error('Camera not available. Use manual entry.');
        setScannerActive(false);
      }
    }, 100);
  };

  const stopScanner = () => {
    html5ScannerRef.current?.clear().catch(() => {});
    setScannerActive(false);
  };

  useEffect(() => () => { html5ScannerRef.current?.clear().catch(() => {}); }, []);

  const handleVerify = async (qrData) => {
    if (!qrData.trim()) return;
    setScanning(true);
    setResult(null);
    setBillTotal('');
    try {
      const res = await api.get(`/api/merchant/verify?qr_data=${encodeURIComponent(qrData.trim())}`);
      setResult({ ...res.data, qr_data: qrData.trim(), type: 'valid' });
      setRedeemAmount(String(res.data.balance || res.data.amount || ''));
    } catch (err) {
      setResult({ type: 'error', message: err.displayMessage || 'Invalid voucher' });
    } finally {
      setScanning(false);
    }
  };

  const handleRedeem = async () => {
    if (!result?.qr_data) return;
    const bill = parseFloat(billTotal);
    const balance = result.balance || result.amount;
    // If bill total provided, auto-calculate redeem amount
    let amt;
    if (bill > 0) {
      amt = Math.min(bill, balance);
    } else {
      amt = parseFloat(redeemAmount);
    }
    if (isNaN(amt) || amt <= 0) { toast.error('Enter a valid bill total or redeem amount'); return; }
    if (amt > balance) { toast.error(`Amount exceeds balance (${formatMoney(balance)})`); return; }
    setConfirming(true);
    try {
      const payload = { qr_data: result.qr_data, redeem_amount: amt };
      if (bill > 0) payload.bill_total = bill;
      const res = await api.post('/api/merchant/redeem', payload);
      setResult({ type: 'redeemed', ...res.data });
      toast.success(`Redeemed ${formatMoney(amt)}!`);
    } catch (err) {
      toast.error(err.displayMessage || 'Redemption failed');
    } finally {
      setConfirming(false);
    }
  };

  const reset = () => {
    setResult(null);
    setManualCode('');
    setBillTotal('');
    setRedeemAmount('');
    setScannerActive(false);
  };

  return (
    <div className="flex flex-col h-full p-4" data-testid="scan-tab">
      {!result ? (
        <>
          {/* Scanner view */}
          <div className="flex-1 flex flex-col items-center justify-center">
            {!scannerActive ? (
              <div className="w-full max-w-sm text-center">
                <div className="w-20 h-20 bg-zinc-800 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <QrCode size={40} className="text-[#EC1A23]" />
                </div>
                <h2 className="text-white text-xl font-bold font-heading mb-2">Scan Voucher</h2>
                <p className="text-zinc-400 text-sm mb-8">Scan a QR code or enter the voucher code manually</p>
                <button
                  onClick={startScanner}
                  className="w-full h-14 text-lg font-bold rounded-2xl bg-[#EC1A23] text-white shadow-[0_0_20px_rgba(236,26,35,0.4)] hover:bg-[#C4121A] transition-all mb-4 active:scale-95"
                  data-testid="start-scan-btn"
                >
                  Open Camera
                </button>

                {/* Manual entry */}
                <div className="mt-4">
                  <p className="text-zinc-500 text-xs mb-3">— or enter manually —</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={manualCode}
                      onChange={e => setManualCode(e.target.value)}
                      placeholder="GVOUCHER:CODE:AMOUNT:HMAC"
                      className="flex-1 bg-zinc-800 border border-zinc-700 text-white text-xs rounded-xl px-3 py-3 outline-none focus:border-[#EC1A23] placeholder:text-zinc-600 font-mono"
                      data-testid="manual-code-input"
                    />
                    <button
                      onClick={() => handleVerify(manualCode)}
                      disabled={!manualCode.trim() || scanning}
                      className="bg-[#EC1A23] text-white px-4 py-3 rounded-xl font-medium text-sm disabled:opacity-50 min-w-[70px]"
                      data-testid="verify-manual-btn"
                    >
                      {scanning ? <Loader2 size={16} className="animate-spin" /> : 'Verify'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-full max-w-sm">
                <div id="qr-reader" className="rounded-2xl overflow-hidden" />
                <button onClick={stopScanner} className="w-full mt-4 border border-zinc-700 text-zinc-300 py-3 rounded-xl text-sm">
                  Cancel Scan
                </button>
              </div>
            )}
          </div>
        </>
      ) : (
        /* Result view */
        <div className="flex-1 flex flex-col items-center justify-center p-2">
          {result.type === 'valid' && (
            <div className="w-full max-w-sm text-center">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-emerald-400" />
              </div>
              <h3 className="text-white text-xl font-bold font-heading mb-1">Valid Voucher</h3>

              {/* Restriction Warning */}
              {result.restriction_warning && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 my-3">
                  <p className="text-amber-400 text-xs font-medium">{result.restriction_warning}</p>
                </div>
              )}

              <div className="bg-zinc-800 rounded-2xl p-5 my-5 text-left space-y-3 border border-zinc-700">
                <div className="flex justify-between items-center">
                  <span className="text-zinc-400 text-sm">Available Balance</span>
                  <span className="text-emerald-400 text-2xl font-bold">{formatMoney(result.balance || result.amount)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-zinc-400 text-sm">Type</span>
                  <span className="text-white text-sm">{result.voucher_type}</span>
                </div>
                {result.voucher_category && result.voucher_category !== 'gift' && (
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-400 text-sm">Category</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-700 text-zinc-300 capitalize">{result.voucher_category.replace('_', ' ')}</span>
                  </div>
                )}
                {result.owner_name && (
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-400 text-sm">Customer</span>
                    <span className="text-white text-sm">{result.owner_name}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-zinc-400 text-sm">Code</span>
                  <span className="text-zinc-300 text-xs font-mono">{result.code}</span>
                </div>
              </div>

              {/* Bill Total Input */}
              <div className="mb-3">
                <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-2">Customer's Bill Total ({symbol})</label>
                <input
                  type="number"
                  value={billTotal}
                  onChange={e => {
                    setBillTotal(e.target.value);
                    const bill = parseFloat(e.target.value);
                    const bal = result.balance || result.amount;
                    if (bill > 0) setRedeemAmount(String(Math.min(bill, bal)));
                    else setRedeemAmount(String(bal));
                  }}
                  placeholder="Enter the bill amount"
                  min="0"
                  step="1"
                  className="w-full bg-zinc-800 border border-zinc-600 text-white text-lg font-bold rounded-xl px-4 py-3 outline-none focus:border-[#D4AF37] text-center"
                  data-testid="bill-total-input"
                />
              </div>

              {/* Breakdown */}
              {parseFloat(billTotal) > 0 && (
                <div className="bg-zinc-800/50 rounded-xl p-3 mb-3 border border-zinc-700/50 space-y-2">
                  {(() => {
                    const bill = parseFloat(billTotal);
                    const bal = result.balance || result.amount;
                    const applied = Math.min(bill, bal);
                    const customerPays = Math.max(0, bill - bal);
                    const remaining = Math.max(0, bal - bill);
                    return (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-zinc-400">Voucher Applied</span>
                          <span className="text-[#D4AF37] font-bold">{formatMoney(applied)}</span>
                        </div>
                        {customerPays > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-zinc-400">Customer Pays Cash</span>
                            <span className="text-amber-400 font-bold">{formatMoney(customerPays)}</span>
                          </div>
                        )}
                        {remaining > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-zinc-400">Balance After</span>
                            <span className="text-emerald-400 font-bold">{formatMoney(remaining)}</span>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Manual override for redeem amount (collapsible) */}
              {!parseFloat(billTotal) && (
                <div className="mb-4">
                  <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-2">— or set redeem amount directly —</label>
                  <input
                    type="number"
                    value={redeemAmount}
                    onChange={e => setRedeemAmount(e.target.value)}
                    min="0.01"
                    max={result.balance || result.amount}
                    step="0.01"
                    className="w-full bg-zinc-800 border border-zinc-600 text-white text-lg font-bold rounded-xl px-4 py-3 outline-none focus:border-[#EC1A23] text-center"
                    data-testid="redeem-amount-input"
                  />
                  <p className="text-zinc-500 text-xs text-center mt-1">
                    Max: {formatMoney(result.balance || result.amount)}
                  </p>
                </div>
              )}

              <button
                onClick={handleRedeem}
                disabled={confirming || !!result.restriction_warning}
                className="w-full h-14 text-lg font-bold rounded-2xl bg-[#EC1A23] text-white shadow-[0_0_20px_rgba(236,26,35,0.4)] hover:bg-[#C4121A] transition-all flex items-center justify-center gap-2 active:scale-95 mb-3 disabled:opacity-50 disabled:shadow-none"
                data-testid="confirm-redeem-btn"
              >
                {confirming ? <Loader2 size={20} className="animate-spin" /> : <><CheckCircle size={20} /> Confirm Redemption</>}
              </button>
              <button onClick={reset} className="w-full border border-zinc-700 text-zinc-300 py-3 rounded-xl text-sm" data-testid="cancel-redeem-btn">Cancel</button>
            </div>
          )}

          {result.type === 'redeemed' && (
            <div className="w-full max-w-sm text-center">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-emerald-400" />
              </div>
              <h3 className="text-white text-2xl font-bold mb-2">
                {result.fully_redeemed !== false ? 'Redeemed!' : 'Partially Redeemed!'}
              </h3>
              <div className="bg-zinc-800 rounded-xl p-4 my-4 border border-zinc-700 space-y-2">
                {result.bill_total > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Bill Total</span>
                    <span className="text-white font-medium">{formatMoney(result.bill_total)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Voucher Applied</span>
                  <span className="text-[#D4AF37] font-bold">{formatMoney(result.amount_redeemed ?? result.amount)}</span>
                </div>
                {result.customer_pays_cash > 0 && (
                  <div className="flex justify-between text-sm border-t border-zinc-700 pt-2">
                    <span className="text-amber-400 font-medium">Customer Pays Cash</span>
                    <span className="text-amber-400 font-bold">{formatMoney(result.customer_pays_cash)}</span>
                  </div>
                )}
                {result.remaining_balance > 0 && (
                  <div className="flex justify-between text-sm border-t border-zinc-700 pt-2">
                    <span className="text-zinc-400">Remaining Balance</span>
                    <span className="text-emerald-400 font-bold">{formatMoney(result.remaining_balance)}</span>
                  </div>
                )}
              </div>
              <button onClick={reset} className="w-full h-12 bg-zinc-800 text-white rounded-xl font-medium" data-testid="scan-another-btn">
                Scan Another
              </button>
            </div>
          )}

          {result.type === 'error' && (
            <div className="w-full max-w-sm text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle size={32} className="text-red-400" />
              </div>
              <h3 className="text-white text-xl font-bold font-heading mb-2">Invalid Voucher</h3>
              <p className="text-zinc-400 text-sm mb-6">{result.message}</p>
              <button onClick={reset} className="w-full h-12 bg-zinc-800 text-white rounded-xl font-medium" data-testid="try-again-btn">Try Again</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HistoryTab() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const { formatMoney, formatDateTime } = useCurrency();

  useEffect(() => {
    api.get('/api/merchant/history')
      .then(res => setHistory(res.data.redemptions))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="p-4" data-testid="history-tab">
      <h2 className="text-white font-heading text-lg font-bold mb-4">Redemption History</h2>
      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-zinc-800 rounded-xl animate-pulse" />)}</div>
      ) : history.length === 0 ? (
        <div className="text-center py-16">
          <History size={40} className="text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500">No redemptions yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {history.map(r => (
            <div key={r.id} className="bg-zinc-800 rounded-xl p-4 border border-zinc-700" data-testid="history-item">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-white font-mono text-sm font-bold tracking-wider">{r.voucher_code}</p>
                  <p className="text-zinc-400 text-xs mt-0.5">{r.voucher_type_name || 'Voucher'}</p>
                  {r.store_name && <p className="text-zinc-500 text-xs">{r.store_name}</p>}
                </div>
                <div className="text-right">
                  <p className="text-[#D4AF37] font-heading font-bold">{formatMoney(r.amount)}</p>
                  <p className="text-zinc-500 text-xs mt-0.5">{formatDateTime(r.created_at)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PlatinumScanTab() {
  const [scannerActive, setScannerActive] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [billTotal, setBillTotal] = useState('');
  const [confirming, setConfirming] = useState(false);
  const html5ScannerRef = useRef(null);
  const { symbol, formatMoney } = useCurrency();

  const startScanner = async () => {
    setScannerActive(true);
    setTimeout(async () => {
      try {
        const { Html5QrcodeScanner } = await import('html5-qrcode');
        html5ScannerRef.current = new Html5QrcodeScanner('plat-qr-reader', { fps: 10, qrbox: { width: 250, height: 250 } }, false);
        html5ScannerRef.current.render(
          (decodedText) => { html5ScannerRef.current?.clear().catch(() => {}); setScannerActive(false); handleVerify(decodedText); },
          () => {}
        );
      } catch { toast.error('Camera not available'); setScannerActive(false); }
    }, 100);
  };

  useEffect(() => () => { html5ScannerRef.current?.clear().catch(() => {}); }, []);

  const handleVerify = async (qrData) => {
    if (!qrData.trim()) return;
    setScanning(true); setResult(null); setBillTotal('');
    try {
      const res = await api.post('/api/membership/platinum/verify', { qr_data: qrData.trim() });
      setResult({ ...res.data, member_id: res.data.member_id, type: 'valid' });
    } catch (err) {
      setResult({ type: 'error', message: err.displayMessage || 'Invalid Platinum QR' });
    } finally { setScanning(false); }
  };

  const handleVerifyByCard = async () => {
    const clean = manualCode.replace(/\s/g, '').replace(/-/g, '');
    if (clean.length !== 16 || !/^\d+$/.test(clean)) {
      toast.error('Enter a valid 16-digit card number');
      return;
    }
    setScanning(true); setResult(null); setBillTotal('');
    try {
      const res = await api.post('/api/membership/platinum/verify-by-card', { card_number: clean });
      setResult({ ...res.data, card_number: clean, member_id: res.data.member_id, type: 'valid' });
    } catch (err) {
      setResult({ type: 'error', message: err.displayMessage || 'Card not found' });
    } finally { setScanning(false); }
  };

  const handleApply = async () => {
    const bill = parseFloat(billTotal);
    if (isNaN(bill) || bill <= 0) { toast.error('Enter a valid bill total'); return; }
    setConfirming(true);
    try {
      const payload = { bill_total: bill };
      if (result.card_number) payload.card_number = result.card_number;
      else if (result.qr_data) payload.qr_data = result.qr_data;
      else if (result.member_id) payload.card_number = result.card_number || '';
      const res = await api.post('/api/membership/platinum/apply-discount', payload);
      setResult({ type: 'applied', ...res.data });
      toast.success(`Discount applied! Customer pays ${formatMoney(res.data.customer_pays)}`);
    } catch (err) {
      toast.error(err.displayMessage || 'Failed to apply discount');
    } finally { setConfirming(false); }
  };

  const reset = () => { setResult(null); setManualCode(''); setBillTotal(''); setScannerActive(false); };

  return (
    <div className="flex flex-col h-full p-4" data-testid="platinum-scan-tab">
      {!result ? (
        <div className="flex-1 flex flex-col items-center justify-center">
          {!scannerActive ? (
            <div className="w-full max-w-sm text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-[#D4AF37] to-[#8B6914] rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Crown size={40} className="text-white" />
              </div>
              <h2 className="text-[#D4AF37] text-xl font-bold font-heading mb-2">Platinum Scan</h2>
              <p className="text-zinc-400 text-sm mb-8">Scan a Platinum member QR to apply store discount</p>
              <button onClick={startScanner} className="w-full h-14 text-lg font-bold rounded-2xl bg-gradient-to-r from-[#D4AF37] to-[#c9a030] text-white shadow-[0_0_20px_rgba(212,175,55,0.3)] hover:from-[#c9a030] hover:to-[#D4AF37] transition-all mb-4 active:scale-95" data-testid="start-platinum-scan-btn">
                Open Camera
              </button>
              <div className="mt-4">
                <p className="text-zinc-500 text-xs mb-3">— or enter card number manually —</p>
                <div className="flex gap-2">
                  <input type="text" value={manualCode} onChange={e => {
                    // Auto-format: XXXX XXXX XXXX XXXX
                    const raw = e.target.value.replace(/\D/g, '').slice(0, 16);
                    const formatted = raw.replace(/(.{4})/g, '$1 ').trim();
                    setManualCode(formatted);
                  }} placeholder="XXXX XXXX XXXX XXXX" maxLength={19}
                    className="flex-1 bg-zinc-800 border border-zinc-700 text-white text-sm rounded-xl px-3 py-3 outline-none focus:border-[#D4AF37] placeholder:text-zinc-600 font-mono tracking-wider" data-testid="manual-platinum-input" />
                  <button onClick={handleVerifyByCard} disabled={manualCode.replace(/\s/g, '').length !== 16 || scanning}
                    className="bg-[#D4AF37] text-white px-4 py-3 rounded-xl font-medium text-sm disabled:opacity-50 min-w-[70px]" data-testid="verify-platinum-btn">
                    {scanning ? <Loader2 size={16} className="animate-spin" /> : 'Verify'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="w-full max-w-sm">
              <div id="plat-qr-reader" className="rounded-2xl overflow-hidden" />
              <button onClick={() => { html5ScannerRef.current?.clear().catch(() => {}); setScannerActive(false); }} className="w-full mt-4 border border-zinc-700 text-zinc-300 py-3 rounded-xl text-sm">
                Cancel Scan
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center p-2">
          {result.type === 'valid' && (
            <div className="w-full max-w-sm text-center">
              <div className="w-16 h-16 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Crown size={32} className="text-[#D4AF37]" />
              </div>
              <h3 className="text-[#D4AF37] text-xl font-bold font-heading mb-1">Platinum Member</h3>
              <div className="bg-zinc-800 rounded-2xl p-5 my-5 text-left space-y-3 border border-[#D4AF37]/20">
                <div className="flex justify-between items-center">
                  <span className="text-zinc-400 text-sm">Member</span>
                  <span className="text-white font-medium">{result.member_name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-zinc-400 text-sm">Tier</span>
                  <span className="text-[#D4AF37] text-sm font-bold uppercase">{result.tier}</span>
                </div>
                {result.store_name && (
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-400 text-sm">Your Store</span>
                    <span className="text-white text-sm">{result.store_name}</span>
                  </div>
                )}
                <div className="flex justify-between items-center border-t border-zinc-700 pt-3">
                  <span className="text-zinc-400 text-sm">Discount</span>
                  <span className="text-[#D4AF37] text-2xl font-bold">{result.discount_percent}%</span>
                </div>
              </div>
              <div className="mb-3">
                <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-2">Customer's Bill Total ({symbol})</label>
                <input type="number" value={billTotal} onChange={e => setBillTotal(e.target.value)} placeholder="Enter bill amount" min="0" step="1"
                  className="w-full bg-zinc-800 border border-zinc-600 text-white text-lg font-bold rounded-xl px-4 py-3 outline-none focus:border-[#D4AF37] text-center" data-testid="platinum-bill-total-input" />
              </div>
              {parseFloat(billTotal) > 0 && result.discount_percent > 0 && (() => {
                const bill = parseFloat(billTotal);
                const disc = round2(bill * result.discount_percent / 100);
                const pays = round2(bill - disc);
                return (
                  <div className="bg-zinc-800/50 rounded-xl p-3 mb-3 border border-[#D4AF37]/20 space-y-2">
                    <div className="flex justify-between text-sm"><span className="text-zinc-400">Discount ({result.discount_percent}%)</span><span className="text-[#D4AF37] font-bold">-{formatMoney(disc)}</span></div>
                    <div className="flex justify-between text-sm border-t border-zinc-700 pt-2"><span className="text-white font-medium">Customer Pays</span><span className="text-white text-lg font-bold">{formatMoney(pays)}</span></div>
                  </div>
                );
              })()}
              <button onClick={handleApply} disabled={confirming || !parseFloat(billTotal)} className="w-full h-14 text-lg font-bold rounded-2xl bg-gradient-to-r from-[#D4AF37] to-[#c9a030] text-white shadow-[0_0_20px_rgba(212,175,55,0.3)] transition-all flex items-center justify-center gap-2 active:scale-95 mb-3 disabled:opacity-50 disabled:shadow-none" data-testid="apply-platinum-discount-btn">
                {confirming ? <Loader2 size={20} className="animate-spin" /> : <><CheckCircle size={20} /> Apply Discount</>}
              </button>
              <button onClick={reset} className="w-full border border-zinc-700 text-zinc-300 py-3 rounded-xl text-sm">Cancel</button>
            </div>
          )}
          {result.type === 'applied' && (
            <div className="w-full max-w-sm text-center">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-emerald-400" />
              </div>
              <h3 className="text-white text-2xl font-bold mb-2">Discount Applied!</h3>
              <div className="bg-zinc-800 rounded-xl p-4 my-4 border border-[#D4AF37]/20 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-zinc-400">Bill Total</span><span className="text-white font-medium">{formatMoney(result.bill_total)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-zinc-400">Platinum Discount ({result.discount_percent}%)</span><span className="text-[#D4AF37] font-bold">-{formatMoney(result.discount_amount)}</span></div>
                <div className="flex justify-between text-sm border-t border-zinc-700 pt-2"><span className="text-white font-medium">Customer Pays</span><span className="text-emerald-400 text-lg font-bold">{formatMoney(result.customer_pays)}</span></div>
              </div>
              <button onClick={reset} className="w-full h-12 bg-zinc-800 text-white rounded-xl font-medium" data-testid="scan-another-platinum-btn">Scan Another</button>
            </div>
          )}
          {result.type === 'error' && (
            <div className="w-full max-w-sm text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4"><XCircle size={32} className="text-red-400" /></div>
              <h3 className="text-white text-xl font-bold font-heading mb-2">Invalid Platinum QR</h3>
              <p className="text-zinc-400 text-sm mb-6">{result.message}</p>
              <button onClick={reset} className="w-full h-12 bg-zinc-800 text-white rounded-xl font-medium">Try Again</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const round2 = (n) => Math.round(n * 100) / 100;

function StatsTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { formatMoney } = useCurrency();

  useEffect(() => {
    api.get('/api/merchant/stats')
      .then(res => setStats(res.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="p-4" data-testid="stats-tab">
      <h2 className="text-white font-heading text-lg font-bold mb-4">My Stats</h2>
      {loading ? (
        <div className="grid grid-cols-2 gap-3">{[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-zinc-800 rounded-xl animate-pulse" />)}</div>
      ) : (
        <div className="space-y-4">
          {stats?.store_name && (
            <div className="bg-zinc-800 rounded-xl p-4 border border-zinc-700 flex items-center gap-3">
              <div className="w-10 h-10 bg-[#561D1A] rounded-xl flex items-center justify-center">
                <BarChart2 size={18} className="text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-zinc-400 text-xs">Assigned Store</p>
                <p className="text-white font-medium">{stats.store_name}</p>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: "Today's Redemptions", value: stats?.today_count || 0, suffix: '' },
              { label: "Today's Amount", value: `${formatMoney(stats?.today_amount || 0)}`, suffix: '' },
              { label: 'Total Redeemed', value: stats?.total_redeemed || 0, suffix: '' },
              { label: 'Total Amount', value: `${formatMoney(stats?.total_amount || 0)}`, suffix: '' },
            ].map(item => (
              <div key={item.label} className="bg-zinc-800 rounded-xl p-4 border border-zinc-700" data-testid="stats-card">
                <p className="text-zinc-400 text-xs mb-1">{item.label}</p>
                <p className="text-white font-heading font-bold text-xl">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function MerchantPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('scan');

  const handleLogout = () => { logout(); navigate('/'); };

  const tabs = [
    { id: 'scan', label: 'Scan', icon: QrCode },
    { id: 'platinum', label: 'Platinum', icon: Crown },
    { id: 'history', label: 'History', icon: History },
    { id: 'stats', label: 'Stats', icon: BarChart2 },
  ];

  return (
    <div className="min-h-screen bg-[#09090b] flex flex-col" style={{ fontFamily: 'Roboto, sans-serif' }} data-testid="merchant-page">
      {/* Header */}
      <header className="bg-zinc-900 border-b border-zinc-800 px-4 py-3 flex items-center justify-between">
        <div>
          <p className="text-white font-heading font-bold text-base">Merchant Portal</p>
          <p className="text-zinc-500 text-xs">{user?.name}</p>
        </div>
        <button onClick={handleLogout} className="text-zinc-500 hover:text-white p-2 rounded-xl hover:bg-zinc-800 transition-colors" data-testid="merchant-logout">
          <LogOut size={18} />
        </button>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto pb-20">
        {tab === 'scan' && <ScanTab storeInfo={user} />}
        {tab === 'platinum' && <PlatinumScanTab />}
        {tab === 'history' && <HistoryTab />}
        {tab === 'stats' && <StatsTab />}
      </main>

      {/* Bottom navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-zinc-900 border-t border-zinc-800 px-4 py-2 safe-area-pb" data-testid="merchant-bottom-nav">
        <div className="flex justify-around max-w-sm mx-auto">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex flex-col items-center gap-1 py-2 px-5 rounded-xl transition-all ${
                tab === id ? 'text-[#EC1A23]' : 'text-zinc-500 hover:text-zinc-300'
              }`}
              data-testid={`merchant-nav-${id}`}
            >
              <Icon size={22} strokeWidth={tab === id ? 2 : 1.5} />
              <span className="text-xs font-medium">{label}</span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
